import { useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, BadgeCheck, MoreHorizontal, Pencil, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getCatalogoFarmacosControllerListarQueryKey,
  getCatalogoFarmacosControllerSugestoesQueryKey,
  useCatalogoFarmacosControllerAtualizar,
  useCatalogoFarmacosControllerSugestoes,
} from '@/api/generated/admin-catalogo-farmacos/admin-catalogo-farmacos';
import type { CatalogoFarmacoResponse } from '@/api/generated/model';
import { AtualizarFarmacoDtoOrigem } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { FarmacoFormDialogo } from '@/features/admin/catalogo-farmacos/components/FarmacoFormDialogo';
import { FundirFarmacoDialogo } from '@/features/admin/catalogo-farmacos/components/FundirFarmacoDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { rotaDoFarmacoDoCatalogo } from '@/shared/lib/rotas';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

/**
 * SUGESTÕES DO CLÍNICO — a fila de curadoria do catálogo de fármacos (P6).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ **Esta tela é o que torna aceitável deixar o clínico criar fármaco.**
 *
 *  O que a criação troca é dado AUSENTE por dado RUIM COM ETIQUETA: em vez de
 *  `nomeLivre`, que nunca entra em filtro na Análise Comparativa, uma linha de
 *  catálogo — imperfeita, mas filtrável desde já. A troca só compensa enquanto
 *  o "ruim" for CONTIDO. Sem a fila, o catálogo global enche de "trilostano",
 *  "Trilostano" e "trilostan" sem ninguém para unificá-los, e um catálogo assim
 *  é pior que texto livre: PARECE padronizado, e a coorte nasce partida em três
 *  sem nenhum sintoma.
 *
 *  ⚠️ **Nenhuma ação nova.** As duas saídas são as que o módulo já tinha:
 *   · é o MESMO princípio com outra grafia → **Fundir em outro**. Os
 *     tratamentos antigos continuam apontando para a linha antiga e a LEITURA
 *     segue a seta; a coorte não parte em duas.
 *   · é um princípio A MAIS → complete sinônimos e classe em **Editar** e use
 *     **Promover a curadoria**. A linha sai da fila.
 *
 *  É o mesmo desenho de `PendenciasLaboratorioPage`, e pelo mesmo motivo: o que
 *  não casa não é descartado nem aceito em silêncio — fica visível, CONTADO, e
 *  com as saídas à mão.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function SugestoesDoClinico() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [aFundir, setAFundir] = useState<CatalogoFarmacoResponse | null>(null);
  const [emEdicao, setEmEdicao] = useState<CatalogoFarmacoResponse | null>(null);

  const { data, isPending, isError, refetch } = useCatalogoFarmacosControllerSugestoes();

  /*
   * ⚠️ Não existe rota de "aprovar": aprovar é EDITAR. A promoção é o mesmo
   * `PATCH` do catálogo, com `origem` junto — e é por isso que ela cabe na
   * mesma linha em que o admin acabou de completar sinônimos e classe.
   */
  const promover = useCatalogoFarmacosControllerAtualizar();

  const podeEditar = pode('admin_catalogo_farmacos.editar');

  async function recarregar() {
    await queryClient.invalidateQueries({
      queryKey: getCatalogoFarmacosControllerSugestoesQueryKey(),
    });
    await queryClient.invalidateQueries({
      queryKey: getCatalogoFarmacosControllerListarQueryKey(),
    });
  }

  async function promoverACuradoria(farmaco: CatalogoFarmacoResponse) {
    try {
      await promover.mutateAsync({
        id: farmaco.id,
        data: { origem: AtualizarFarmacoDtoOrigem.CURADORIA },
      });
      await recarregar();
      toast.success(`"${farmaco.nome}" agora faz parte do catálogo curado.`);
    } catch (erro) {
      // ⚠️ A mensagem do servidor vem escrita em linguagem clínica (o 409 de
      // nome duplicado, por exemplo). Trocá-la por um genérico joga fora a
      // única informação que diz o que fazer a seguir.
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível promover a sugestão.',
      );
    }
  }

  if (isPending) {
    return (
      <div className="p-6 space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 rounded-md" />
        ))}
      </div>
    );
  }

  if (isError || !data) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  if (data.itens.length === 0) {
    return (
      <EstadoVazio
        icone={Sparkles}
        titulo="Nenhuma sugestão pendente"
        descricao="Quando um veterinário digitar um fármaco que não está no catálogo, ele aparece aqui para você fundir ou curar."
      />
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fármaco sugerido</TableHead>
              <TableHead>Em uso</TableHead>
              <TableHead>Sugerido em</TableHead>
              <TableHead>Situação</TableHead>
              <TableHead className="w-20">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.itens.map((farmaco) => (
              <TableRow key={farmaco.id}>
                <TableCell>
                  <Link
                    to={rotaDoFarmacoDoCatalogo(farmaco.id)}
                    className="font-medium text-foreground hover:text-brand transition-colors"
                  >
                    {farmaco.nome}
                  </Link>

                  {farmaco.classeTerapeutica ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {farmaco.classeTerapeutica}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Sem classe nem sinônimos — foi digitado no meio de um atendimento.
                    </p>
                  )}

                  {farmaco.substituidoPorNome && (
                    <p className="text-xs text-amber-600 mt-0.5">
                      → fundido em {farmaco.substituidoPorNome}
                    </p>
                  )}
                </TableCell>

                {/* ⚠️ É por este número que se decide o que fundir primeiro:
                    uma sugestão com 40 tratamentos é uma coorte de 40 pacientes
                    esperando dois cliques. */}
                <TableCell className="text-muted-foreground tabular-nums">
                  <span title={`${farmaco.totalEmUso} tratamento(s) já usam esta entrada`}>
                    {farmaco.totalEmUso}
                  </span>
                </TableCell>

                <TableCell className="text-muted-foreground">
                  {new Date(farmaco.criadoEm).toLocaleDateString('pt-BR')}
                </TableCell>

                <TableCell>
                  {farmaco.ativo ? (
                    <Badge variant="outline" className="text-amber-600 border-amber-600/40">
                      Aguardando curadoria
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-destructive border-destructive/40">
                      Desativada
                    </Badge>
                  )}
                </TableCell>

                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={`Ações de ${farmaco.nome}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {podeEditar ? (
                        <>
                          <DropdownMenuItem onClick={() => setEmEdicao(farmaco)}>
                            <Pencil className="w-4 h-4" />
                            Completar sinônimos e classe
                          </DropdownMenuItem>

                          {/* Item já fundido não funde de novo — o servidor
                              recusa o destino já fundido, e oferecer a ação
                              seria oferecer um 409. */}
                          {farmaco.ativo && !farmaco.substituidoPorId && (
                            <DropdownMenuItem onClick={() => setAFundir(farmaco)}>
                              <ArrowRightLeft className="w-4 h-4" />É duplicata — fundir em outro
                            </DropdownMenuItem>
                          )}

                          <DropdownMenuItem onClick={() => void promoverACuradoria(farmaco)}>
                            <BadgeCheck className="w-4 h-4" />
                            Promover a curadoria
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <DropdownMenuItem disabled>
                          Você não tem permissão para curar o catálogo.
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {aFundir && (
        <FundirFarmacoDialogo aberto aoFechar={() => setAFundir(null)} farmaco={aFundir} />
      )}

      {emEdicao && (
        <FarmacoFormDialogo aberto aoFechar={() => setEmEdicao(null)} farmaco={emEdicao} />
      )}
    </>
  );
}
